import { useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router"
import { useLocation } from "react-router-dom"

import { RoutesPaths } from "../constants/routes.ts"
import { getVisibleProjectTabs } from "../containers/Project/helpers/project-visible-tabs.ts"
import { type PermissionProjectPermissions } from "../containers/SettingsPermissions/types/permissions.types.ts"
import { useAppSelector } from "../hooks/use-app-selector.ts"

interface ProjectPageGuardProps {
  permissions: PermissionProjectPermissions
}

export const ProjectPageNavigation = ({
  permissions
}: ProjectPageGuardProps) => {
  const { id } = useParams<{ id: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)

  /**
   * The same list the tab strip renders from. It used to be a second copy, and
   * the copies drifted: this one never learned that subcontractors do not get
   * Invoices, so it treated that page as allowed — and, being first in its own
   * map, could redirect a subcontractor onto it.
   */
  const allowedPaths = useMemo(() => {
    if (!id) return []
    const routes = RoutesPaths.project.view(id)
    return getVisibleProjectTabs(permissions, roleId).map((tab) => routes[tab])
  }, [id, permissions, roleId])

  const isCurrentPageAllowed = useMemo(
    () => allowedPaths.some((path) => pathname.includes(path)),
    [allowedPaths, pathname]
  )

  const [firstAllowedPage] = allowedPaths

  useEffect(() => {
    if (isCurrentPageAllowed) return
    if (!firstAllowedPage) return
    if (firstAllowedPage === pathname) return // already there, avoid loop

    navigate(firstAllowedPage, { replace: true })
  }, [isCurrentPageAllowed, firstAllowedPage, pathname, navigate])

  return null
}
